import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  LogIn, LogOut, Search, Download, RefreshCw, Phone, Mail, MapPin,
  Calendar, X, Filter, BarChart3,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTH_KEY = "swifthaul_admin_auth";

const STATUS_PILLS = {
  new:       { label: "New",       color: "bg-blue-100 text-blue-800 border-blue-300" },
  contacted: { label: "Contacted", color: "bg-purple-100 text-purple-800 border-purple-300" },
  quoted:    { label: "Quoted",    color: "bg-amber-100 text-amber-900 border-amber-300" },
  booked:    { label: "Booked",    color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  completed: { label: "Completed", color: "bg-stone-200 text-stone-800 border-stone-400" },
  lost:      { label: "Lost",      color: "bg-red-100 text-red-800 border-red-300" },
};

const SERVICE_LABELS = {
  studio: "Studio", "1br": "1 BR", "2br": "2 BR", "3br": "3 BR",
  "4br_plus": "4+ BR", office: "Office", junk: "Junk",
};

function makeAuthHeader(user, pass) {
  return "Basic " + btoa(`${user}:${pass}`);
}

export default function Admin() {
  const [auth, setAuth] = useState(() => sessionStorage.getItem(AUTH_KEY) || "");
  const [loginForm, setLoginForm] = useState({ username: "admin", password: "" });
  const [loginErr, setLoginErr] = useState("");
  const [loading, setLoading] = useState(false);

  // Data
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [selected, setSelected] = useState(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState("");
  const [filterService, setFilterService] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const headers = useMemo(() => (auth ? { Authorization: auth } : {}), [auth]);

  const fetchAll = async () => {
    if (!auth) return;
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterService) params.service = filterService;
      if (searchQ) params.q = searchQ;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const [s, q] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/quotes`, { headers, params }),
      ]);
      setStats(s.data);
      setQuotes(q.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        sessionStorage.removeItem(AUTH_KEY);
        setAuth("");
      } else {
        toast.error("Failed to load data.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginErr("");
    const h = makeAuthHeader(loginForm.username, loginForm.password);
    try {
      await axios.get(`${API}/admin/stats`, { headers: { Authorization: h } });
      sessionStorage.setItem(AUTH_KEY, h);
      setAuth(h);
      toast.success("Welcome back");
    } catch (err) {
      setLoginErr(err?.response?.status === 401 ? "Invalid username or password." : "Login failed.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(AUTH_KEY);
    setAuth("");
    setQuotes([]);
    setStats(null);
    setSelected(null);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quotes/${id}/status`, { status }, { headers });
      setQuotes((q) => q.map((x) => (x.id === id ? { ...x, status } : x)));
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
      toast.success(`Marked as ${STATUS_PILLS[status].label}`);
      // refresh stats
      axios.get(`${API}/admin/stats`, { headers }).then((r) => setStats(r.data)).catch(() => {});
    } catch {
      toast.error("Could not update status.");
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/quotes/export.csv`, { headers, responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `swifthaul-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  const clearFilters = () => {
    setFilterStatus(""); setFilterService(""); setSearchQ(""); setDateFrom(""); setDateTo("");
  };

  // ===== LOGIN VIEW =====
  if (!auth) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center px-4">
        <Card className="w-full max-w-md p-8 bg-white border border-stone-300 rounded-sm shadow-xl">
          <div className="text-center mb-6">
            <div className="inline-block px-3 py-1 bg-stone-950 text-amber-500 text-[10px] tracking-[0.3em] uppercase font-semibold rounded-sm mb-3">
              Admin
            </div>
            <h1 className="font-display text-3xl font-light text-stone-950 tracking-tight">
              Swift<em className="italic text-amber-700">haul</em>
            </h1>
            <p className="text-xs text-stone-500 mt-1 tracking-wide">Lead Console · Sign in to continue</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-stone-700">Username</Label>
              <Input data-testid="admin-username" value={loginForm.username} onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })} autoComplete="username" className="mt-1 h-10 rounded-sm border-stone-300" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-stone-700">Password</Label>
              <Input data-testid="admin-password" type="password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} autoComplete="current-password" className="mt-1 h-10 rounded-sm border-stone-300" />
            </div>
            {loginErr && <p className="text-xs text-red-600">{loginErr}</p>}
            <Button data-testid="admin-login-btn" type="submit" className="w-full bg-stone-950 hover:bg-amber-800 text-stone-50 h-11 rounded-sm uppercase tracking-[0.2em] text-xs font-display">
              <LogIn className="w-4 h-4 mr-2" /> Sign in
            </Button>
          </form>
          <p className="text-[10px] text-stone-400 mt-6 text-center">Authorized personnel only · SWIFTHAUL © {new Date().getFullYear()}</p>
        </Card>
      </div>
    );
  }

  // ===== DASHBOARD VIEW =====
  return (
    <div className="min-h-screen bg-stone-100">
      {/* Header */}
      <header className="bg-stone-950 text-stone-50 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="px-2 py-0.5 bg-amber-500 text-stone-950 text-[10px] tracking-[0.3em] uppercase font-bold rounded-sm">Admin</div>
            <h1 className="font-display text-xl font-light tracking-tight">Swift<em className="italic text-amber-500">haul</em> · Lead Console</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="refresh-btn" variant="ghost" size="sm" onClick={fetchAll} disabled={loading} className="text-stone-300 hover:text-amber-500 hover:bg-white/5">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button data-testid="export-csv-btn" variant="outline" size="sm" onClick={downloadCsv} className="bg-transparent text-stone-300 border-stone-700 hover:bg-amber-700 hover:text-stone-50 hover:border-amber-700">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
            </Button>
            <Button data-testid="logout-btn" variant="ghost" size="sm" onClick={logout} className="text-stone-300 hover:text-red-400 hover:bg-white/5">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-5">
            <StatTile label="Total" value={stats.total} accent="bg-stone-950 text-amber-500" />
            <StatTile label="Today" value={stats.today} accent="bg-amber-700 text-stone-50" />
            {Object.entries(stats.by_status).map(([k, v]) => (
              <StatTile key={k} label={STATUS_PILLS[k]?.label || k} value={v} />
            ))}
          </div>
        )}

        {/* Filter bar */}
        <Card className="p-3 mb-4 bg-white border border-stone-200 rounded-sm">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[180px]">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-stone-600">Search</Label>
              <div className="relative mt-0.5">
                <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-stone-400" />
                <Input data-testid="search-input" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchAll()} placeholder="Name, email, phone…" className="h-8 pl-7 text-xs rounded-sm border-stone-300" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-stone-600">Status</Label>
              <select data-testid="filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="mt-0.5 h-8 text-xs border border-stone-300 rounded-sm bg-white px-2 min-w-[110px]">
                <option value="">All</option>
                {Object.entries(STATUS_PILLS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-stone-600">Service</Label>
              <select data-testid="filter-service" value={filterService} onChange={(e) => setFilterService(e.target.value)} className="mt-0.5 h-8 text-xs border border-stone-300 rounded-sm bg-white px-2 min-w-[110px]">
                <option value="">All</option>
                {Object.entries(SERVICE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-stone-600">From</Label>
              <Input data-testid="filter-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-0.5 h-8 text-xs rounded-sm border-stone-300" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-stone-600">To</Label>
              <Input data-testid="filter-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-0.5 h-8 text-xs rounded-sm border-stone-300" />
            </div>
            <Button data-testid="apply-filters-btn" onClick={fetchAll} size="sm" className="h-8 bg-stone-950 hover:bg-amber-800 text-stone-50 rounded-sm text-[11px] uppercase tracking-[0.15em] px-4">
              <Filter className="w-3 h-3 mr-1.5" /> Apply
            </Button>
            <Button onClick={clearFilters} size="sm" variant="outline" className="h-8 rounded-sm text-[11px] uppercase tracking-[0.15em] px-3 border-stone-300">
              Clear
            </Button>
          </div>
        </Card>

        {/* Table */}
        <Card className="bg-white border border-stone-200 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr className="text-[10px] uppercase tracking-[0.18em] text-stone-600">
                  <th className="text-left px-3 py-2 font-semibold">Ref</th>
                  <th className="text-left px-3 py-2 font-semibold">Status</th>
                  <th className="text-left px-3 py-2 font-semibold">Received</th>
                  <th className="text-left px-3 py-2 font-semibold">Customer</th>
                  <th className="text-left px-3 py-2 font-semibold">Service</th>
                  <th className="text-left px-3 py-2 font-semibold">ZIP</th>
                  <th className="text-left px-3 py-2 font-semibold">Move Date</th>
                  <th className="text-left px-3 py-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {quotes.length === 0 && !loading && (
                  <tr><td colSpan={8} className="px-3 py-12 text-center text-stone-500 text-sm">
                    No leads yet. As soon as a customer submits the quote form, they'll show up here.
                  </td></tr>
                )}
                {quotes.map((q) => {
                  const ref = q.id.slice(0, 8).toUpperCase();
                  const pill = STATUS_PILLS[q.status] || STATUS_PILLS.new;
                  return (
                    <tr key={q.id} className="hover:bg-amber-50/50 cursor-pointer" onClick={() => setSelected(q)} data-testid={`row-${ref}`}>
                      <td className="px-3 py-2.5 font-mono text-xs text-stone-700">#{ref}</td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-sm border ${pill.color}`}>{pill.label}</span>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-stone-600">
                        {new Date(q.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-stone-900">{q.full_name}</div>
                        <div className="text-[11px] text-stone-500">{q.phone}</div>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-stone-700">{SERVICE_LABELS[q.moving_size] || q.moving_size}</td>
                      <td className="px-3 py-2.5 text-xs text-stone-700 font-mono">{q.zip_code || "—"}</td>
                      <td className="px-3 py-2.5 text-xs text-stone-700">{q.move_date || "—"}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                          <a href={`tel:${q.phone}`} className="p-1.5 bg-stone-100 hover:bg-amber-100 rounded-sm" title="Call"><Phone className="w-3 h-3 text-stone-700" /></a>
                          <a href={`mailto:${q.email}`} className="p-1.5 bg-stone-100 hover:bg-amber-100 rounded-sm" title="Email"><Mail className="w-3 h-3 text-stone-700" /></a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </main>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-stone-950/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={() => setSelected(null)}>
          <Card data-testid="lead-detail-modal" className="w-full max-w-2xl bg-white border border-stone-300 rounded-sm shadow-2xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="bg-stone-950 text-stone-50 px-5 py-3 flex items-center justify-between border-b border-stone-800">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-amber-500 font-semibold">Lead #{selected.id.slice(0, 8).toUpperCase()}</div>
                <div className="font-display text-lg font-light italic mt-0.5">{selected.full_name}</div>
              </div>
              <button onClick={() => setSelected(null)} className="p-1.5 hover:bg-white/10 rounded-full"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Status changer */}
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-stone-600 mb-1.5 block">Status</Label>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(STATUS_PILLS).map(([k, v]) => (
                    <button
                      key={k}
                      data-testid={`status-btn-${k}`}
                      onClick={() => updateStatus(selected.id, k)}
                      className={`px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide rounded-sm border transition-all ${
                        selected.status === k ? v.color + " ring-2 ring-offset-1 ring-amber-700" : "bg-white text-stone-600 border-stone-300 hover:border-stone-500"
                      }`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <InfoRow icon={Phone} label="Phone" value={<a href={`tel:${selected.phone}`} className="text-amber-800 hover:underline">{selected.phone}</a>} />
                <InfoRow icon={Mail} label="Email" value={<a href={`mailto:${selected.email}`} className="text-amber-800 hover:underline break-all">{selected.email}</a>} />
                <InfoRow icon={MapPin} label="Service" value={SERVICE_LABELS[selected.moving_size] || selected.moving_size} />
                <InfoRow icon={MapPin} label="Pickup ZIP" value={selected.zip_code || "—"} />
                <InfoRow icon={MapPin} label="Pickup" value={selected.pickup_address} full />
                {selected.destination_address && <InfoRow icon={MapPin} label="Destination" value={selected.destination_address} full />}
                {selected.move_date && <InfoRow icon={Calendar} label="Move date" value={selected.move_date} />}
                <InfoRow icon={Calendar} label="Received" value={new Date(selected.created_at).toLocaleString()} />
              </div>

              {(selected.stairs_pickup !== "none" || selected.stairs_destination !== "none" || selected.heavy_items || selected.long_carry || selected.estimated_distance_miles) && (
                <div className="border-t border-stone-200 pt-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-stone-600 mb-2 font-semibold">Special factors</div>
                  <ul className="text-sm text-stone-700 space-y-1">
                    {selected.stairs_pickup && selected.stairs_pickup !== "none" && <li>Stairs at pickup: <span className="font-medium">{selected.stairs_pickup.replace("_", " ")}</span></li>}
                    {selected.stairs_destination && selected.stairs_destination !== "none" && <li>Stairs at destination: <span className="font-medium">{selected.stairs_destination.replace("_", " ")}</span></li>}
                    {selected.heavy_items && <li>Heavy/special items: <span className="font-medium">Yes</span></li>}
                    {selected.long_carry && <li>Long carry (&gt;50ft): <span className="font-medium">Yes</span></li>}
                    {selected.estimated_distance_miles ? <li>Distance: <span className="font-medium">~{selected.estimated_distance_miles} miles</span></li> : null}
                  </ul>
                </div>
              )}

              {selected.inventory && Object.keys(selected.inventory).length > 0 && (
                <div className="border-t border-stone-200 pt-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-stone-600 mb-2 font-semibold">Inventory</div>
                  <div className="grid grid-cols-2 gap-1 text-sm">
                    {Object.entries(selected.inventory).map(([k, v]) => v > 0 && (
                      <div key={k} className="flex justify-between px-2 py-1 bg-stone-50 rounded-sm">
                        <span className="text-stone-700">{k}</span><span className="font-semibold text-stone-900">×{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selected.notes && (
                <div className="border-t border-stone-200 pt-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-stone-600 mb-1.5 font-semibold">Notes</div>
                  <p className="text-sm text-stone-700 whitespace-pre-wrap">{selected.notes}</p>
                </div>
              )}
            </div>

            <div className="bg-stone-50 px-5 py-3 border-t border-stone-200 flex justify-end gap-2">
              <Button asChild variant="outline" size="sm" className="rounded-sm">
                <a href={`mailto:${selected.email}?subject=Your%20SwiftHaul%20quote%20%23${selected.id.slice(0,8).toUpperCase()}`}>
                  <Mail className="w-3.5 h-3.5 mr-1.5" /> Reply
                </a>
              </Button>
              <Button asChild size="sm" className="bg-stone-950 hover:bg-amber-800 text-stone-50 rounded-sm">
                <a href={`tel:${selected.phone}`}><Phone className="w-3.5 h-3.5 mr-1.5" /> Call</a>
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, accent = "bg-white text-stone-900" }) {
  return (
    <div className={`p-2.5 border border-stone-200 rounded-sm ${accent}`}>
      <div className="text-[9px] uppercase tracking-[0.2em] opacity-70 font-medium">{label}</div>
      <div className="text-xl font-display font-light tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 flex items-center gap-1 mb-0.5">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-sm text-stone-900">{value}</div>
    </div>
  );
}
